import React, { useEffect, useState } from "react";
import { PieChart, Pie, Cell } from "recharts";
import { api } from "@/lib/api";

export const CP = {
  navy: "#12275A", blue: "#2563EB", purple: "#7C5CFC", green: "#10B981",
  amber: "#F59E0B", red: "#EF4444", sky: "#0EA5E9",
  bg: "#F5F7FC", border: "#E4E9F4", text: "#1E293B", sub: "#64748B",
};

export const DOMAIN_COLORS = {
  "Data & Analytics": CP.purple,
  Engineering: CP.blue,
  Business: CP.amber,
  Leadership: CP.green,
  "Tools & Technologies": CP.sky,
};

const TONES = {
  blue: ["#EFF4FF", "#2563EB", "#BFD3FE"],
  purple: ["#F3F0FF", "#6D4AFF", "#D8CEFE"],
  green: ["#ECFDF5", "#059669", "#A7F3D0"],
  amber: ["#FFF7E6", "#B45309", "#FDE68A"],
  red: ["#FEF2F2", "#DC2626", "#FECACA"],
  slate: ["#F1F5F9", "#475569", "#E2E8F0"],
};

export function Pill({ tone = "blue", children, testid }) {
  const [bg, fg, bd] = TONES[tone] || TONES.blue;
  return (
    <span data-testid={testid} className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold"
      style={{ background: bg, color: fg, border: `1px solid ${bd}` }}>{children}</span>
  );
}

export const priorityTone = (p) => (p === "high" ? "red" : p === "medium" ? "amber" : "green");

export function CapCard({ title, subtitle, right, children, className = "", testid }) {
  return (
    <div data-testid={testid} className={`rounded-xl bg-white shadow-sm ${className}`} style={{ border: `1px solid ${CP.border}` }}>
      {(title || right) && (
        <div className="flex items-start justify-between px-4 pt-4 pb-2">
          <div>
            <div className="text-sm font-semibold" style={{ color: CP.text }}>{title}</div>
            {subtitle && <div className="text-xs mt-0.5" style={{ color: CP.sub }}>{subtitle}</div>}
          </div>
          {right}
        </div>
      )}
      <div className="px-4 pb-4">{children}</div>
    </div>
  );
}

export function Kpi({ label, value, delta, deltaLabel = "vs last 6 months", tone = CP.navy, testid }) {
  const up = typeof delta === "number" && delta >= 0;
  return (
    <div data-testid={testid} className="rounded-xl bg-white shadow-sm p-4" style={{ border: `1px solid ${CP.border}` }}>
      <div className="text-xs font-medium" style={{ color: CP.sub }}>{label}</div>
      <div className="text-2xl font-bold mt-1" style={{ color: tone }}>{value}</div>
      {delta !== undefined && (
        <div className="text-[11px] mt-1">
          <span style={{ color: up ? CP.green : CP.red }}>{up ? "▲" : "▼"} {Math.abs(delta)}%</span>
          <span style={{ color: CP.sub }}> {deltaLabel}</span>
        </div>
      )}
    </div>
  );
}

export function HBar({ label, value, max, color = CP.blue, suffix = "", testid }) {
  const pct = Math.min(100, (value / Math.max(max, 1)) * 100);
  return (
    <div className="flex items-center gap-3" data-testid={testid}>
      <div className="w-36 shrink-0 text-xs capitalize truncate" style={{ color: CP.text }}>{label}</div>
      <div className="flex-1 h-2.5 rounded-full" style={{ background: "#EDF1F9" }}>
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: color }} />
      </div>
      <div className="w-14 text-right text-xs font-semibold" style={{ color: CP.text }}>{value}{suffix}</div>
    </div>
  );
}

export function GaugeDonut({ value, max = 5, label, sub, color = CP.green, size = 160, testid }) {
  const pct = Math.min(1, value / max);
  const data = [{ v: pct }, { v: 1 - pct }];
  return (
    <div className="relative flex flex-col items-center" data-testid={testid} style={{ width: size }}>
      <PieChart width={size} height={size}>
        <Pie data={data} dataKey="v" startAngle={90} endAngle={-270}
          innerRadius={size * 0.36} outerRadius={size * 0.46} stroke="none">
          <Cell fill={color} />
          <Cell fill="#EDF1F9" />
        </Pie>
      </PieChart>
      <div className="absolute inset-0 flex flex-col items-center justify-center" style={{ height: size }}>
        <div className="text-2xl font-bold" style={{ color: CP.text }}>{value}{max === 100 ? "%" : ""}</div>
        {max !== 100 && <div className="text-[10px]" style={{ color: CP.sub }}>/ {max}</div>}
      </div>
      {label && <div className="text-sm font-semibold -mt-2" style={{ color }}>{label}</div>}
      {sub && <div className="text-[11px] text-center" style={{ color: CP.sub }}>{sub}</div>}
    </div>
  );
}

const AV_COLORS = ["#2563EB", "#7C5CFC", "#0EA5E9", "#10B981", "#F59E0B", "#EC4899"];
export function InitialAvatar({ name = "?", size = 40 }) {
  const initials = name.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();
  const color = AV_COLORS[(name.charCodeAt(0) || 0) % AV_COLORS.length];
  return (
    <div className="rounded-full flex items-center justify-center font-bold text-white shrink-0"
      style={{ width: size, height: size, background: color, fontSize: size * 0.36 }}>{initials}</div>
  );
}

export function Stars({ value = 0 }) {
  return (
    <span className="text-sm tracking-tight" style={{ color: "#F5B301" }}>
      {"★".repeat(Math.round(Math.min(5, value)))}<span style={{ color: "#E2E8F0" }}>{"★".repeat(5 - Math.round(Math.min(5, value)))}</span>
    </span>
  );
}

export function UserSearch({ onSelect, placeholder = "Search people…", testid = "cap-user-search" }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/capability/users/search", { params: { q: q.trim() } });
        setResults(data || []);
      } catch { /* silent */ }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div className="relative">
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={placeholder} data-testid={testid}
        className="w-full h-10 px-3 rounded-lg text-sm bg-white outline-none focus:ring-2"
        style={{ border: `1px solid ${CP.border}`, color: CP.text }} />
      {results.length > 0 && (
        <div className="absolute z-30 top-full left-0 right-0 mt-1 rounded-lg bg-white shadow-lg max-h-64 overflow-auto"
          style={{ border: `1px solid ${CP.border}` }} data-testid={`${testid}-results`}>
          {results.map((c) => (
            <button key={c.user_id} onClick={() => { onSelect(c); setQ(""); setResults([]); }}
              className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center gap-2"
              data-testid={`${testid}-option-${c.user_id}`}>
              <InitialAvatar name={c.name} size={28} />
              <div>
                <div className="text-sm font-medium" style={{ color: CP.text }}>{c.name}</div>
                <div className="text-[11px]" style={{ color: CP.sub }}>{c.title || c.role} · {c.email}</div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
